import React, { useCallback, useEffect, useState } from 'react';
import { ImageRightsAPI } from '../../api/ImageRightsAPI';
import { formatDay, thumbnail } from './pictureParts';
import SymbolPictureManager, { typeLabel } from './SymbolPictureManager';
import SymbolPreviewDialog from './SymbolPreviewDialog';

const FILTERS = [
  { key: 'OPEN', label: 'Ohne Bild' },
  { key: 'SYMBOL', label: 'Mit KI-Symbolbild' },
  { key: 'ALL', label: 'Alle' },
];

const SHOWING_LABELS = {
  NONE: 'Kein Bild',
  HIDDEN: 'Bild nicht freigegeben',
  SYMBOL: 'KI-Symbolbild',
};

/** The events still showing Schick's artwork: no picture, or one that is not cleared. */
export const openEvents = (data) => (data?.types ?? [])
  .flatMap((type) => type.events)
  .filter((event) => event.showing !== 'SYMBOL');

const matches = (event, filter) => {
  if (filter === 'OPEN') return event.showing !== 'SYMBOL';
  if (filter === 'SYMBOL') return event.showing === 'SYMBOL';
  return true;
};

function EventRow({ event, series, pictures, busy, onAssign, onAssignAll }) {
  // A click shows the picture large; it is set only with "Festlegen" there.
  const [preview, setPreview] = useState(null);
  const closePreview = useCallback(() => setPreview(null), []);
  const time = (event.startTime || '').slice(0, 5);

  return (
    <article className={'isy-row' + (busy ? ' isy-row--busy' : '') + (event.showing === 'SYMBOL' ? ' is-done' : '')}>
      <div className="isy-current">
        {event.image
          ? <img src={thumbnail(event.image)} alt="" loading="lazy" />
          : <span>Schick-Grafik</span>}
      </div>
      <div className="isy-main">
        <div className="isy-head">
          <strong className="isy-title" title={event.title}>{event.title}</strong>
          <span className={'ir-visibility' + (event.showing === 'SYMBOL' ? ' is-on' : '')}>{SHOWING_LABELS[event.showing]}</span>
          {!event.live && <span className="ir-badge">Event wartet auf Freigabe</span>}
        </div>
        <p className="isy-facts">
          {formatDay(event.eventDate)}
          {time ? ` · ${time}` : ''}
          {event.account ? ` · @${event.account}` : ''}
          {event.address ? ` · ${event.address}` : ''}
          {series.length > 0 ? ` · Reihe mit ${series.length + 1} Terminen` : ''}
        </p>
        <div className="isy-picks" role="group" aria-label={`KI-Symbolbild für ${event.title}`}>
          {pictures.map((picture, index) => {
            const chosen = picture.url === event.image;
            return (
              <button
                key={picture.name}
                type="button"
                className={'isy-pick' + (chosen ? ' is-chosen' : '')}
                aria-pressed={chosen}
                aria-label={`${picture.name}${chosen ? ' (gewählt)' : ''} groß ansehen`}
                title={`${picture.name} groß ansehen`}
                disabled={busy}
                onClick={() => setPreview(index)}
              >
                <img src={picture.url} alt="" loading="lazy" />
              </button>
            );
          })}
        </div>
      </div>
      {preview !== null && (
        <SymbolPreviewDialog event={event} series={series} pictures={pictures} startIndex={preview}
          onAssign={onAssign} onAssignAll={onAssignAll} onClose={closePreview} />
      )}
    </article>
  );
}

/**
 * Upcoming events of a kind we have AI symbol pictures for (Rave), that show Schick's artwork
 * today. One click gives an event one of them; the apps label it as an AI picture.
 */
export default function SymbolImages({ onLoaded, onAssigned }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('OPEN');
  // The events that could use a picture, or the pictures themselves to add and remove.
  const [mode, setMode] = useState('EVENTS');
  const [busy, setBusy] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const loaded = await ImageRightsAPI.symbolImages();
      setData(loaded);
      onLoaded?.(loaded);
    } catch (err) {
      console.error('Fehler beim Laden der KI-Symbolbilder:', err);
      setError('Konnte die KI-Symbolbilder nicht laden.');
    } finally {
      setLoading(false);
    }
  }, [onLoaded]);

  useEffect(() => { load(); }, [load]);

  /** Saves a picture for one or several events and puts what the backend answers into the list. */
  const save = async (events, request) => {
    const ids = events.map((each) => each.id);
    setBusy((prev) => new Set([...prev, ...ids]));
    setError(null);
    try {
      const answer = await request();
      const byId = new Map((Array.isArray(answer) ? answer : [answer]).map((each) => [each.id, each]));
      const next = data && {
        ...data,
        types: data.types.map((type) => ({
          ...type,
          events: type.events.map((each) => byId.get(each.id) ?? each),
        })),
      };
      setData(next);
      onLoaded?.(next);
      // A hidden picture replaced here leaves the page's other tabs.
      onAssigned?.();
      return true;
    } catch (err) {
      console.error('Fehler beim Zuweisen:', err);
      setError(`Konnte das Bild bei „${events[0].title}“ nicht speichern.`);
      return false;
    } finally {
      setBusy((prev) => {
        const nextBusy = new Set(prev);
        ids.forEach((id) => nextBusy.delete(id));
        return nextBusy;
      });
    }
  };
  const assign = (event, picture) => save([event], () => ImageRightsAPI.assignSymbolImage(event.id, picture.url));
  const assignAll = (events, picture) =>
    save(events, () => ImageRightsAPI.assignSymbolImageToAll(events.map((each) => each.id), picture.url));

  const types = data?.types ?? [];
  const all = types.flatMap((type) => type.events);

  return (
    <section aria-label="KI-Symbolbilder">
      <div className="ir-views isy-modes" role="tablist" aria-label="Ansicht der KI-Symbolbilder">
        <button type="button" role="tab" aria-selected={mode === 'EVENTS'}
          className={mode === 'EVENTS' ? 'is-active' : ''} onClick={() => setMode('EVENTS')}>
          Events
        </button>
        <button type="button" role="tab" aria-selected={mode === 'MANAGE'}
          className={mode === 'MANAGE' ? 'is-active' : ''} onClick={() => setMode('MANAGE')}>
          Bilder verwalten
        </button>
      </div>

      {mode === 'MANAGE' ? (
        loading && !data
          ? <div className="pp-sentinel"><div className="pp-spinner" aria-label="Lädt" /></div>
          : <SymbolPictureManager data={data} onChanged={load} />
      ) : (<>
      <p className="ir-rule">
        Kommende Events, die gerade die Schick-Grafik zeigen: ohne Bild oder mit einem Bild, das nicht freigegeben ist.
        Ein KI-Symbolbild gibt es nur für Events seiner Kategorie, die App und die Website kennzeichnen es automatisch.
        <strong> Erst zuweisen, wenn die App-Version mit dem KI-Label draußen ist.</strong>
      </p>

      <div className="ir-toolbar">
        <div className="ir-filters" role="tablist" aria-label="Filter">
          {FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              aria-selected={filter === option.key}
              className={'ir-chip' + (filter === option.key ? ' is-active' : '')}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
              <span className="ir-chip-count">{all.filter((event) => matches(event, option.key)).length}</span>
            </button>
          ))}
        </div>
        <div className="ir-toolbar-right">
          <button className="btn-add btn-add--green" type="button" onClick={load} disabled={loading}>
            Neu laden
          </button>
        </div>
      </div>

      {error && <p className="pp-error">{error}</p>}

      {loading && !data ? (
        <div className="pp-sentinel"><div className="pp-spinner" aria-label="Lädt" /></div>
      ) : types.length === 0 ? (
        <div className="pp-empty">Noch keine KI-Symbolbilder. Unter „Bilder verwalten“ lädst du welche hoch.</div>
      ) : types.map((type) => {
        const shown = type.events.filter((event) => matches(event, filter));
        return (
          <div key={type.specialType} className="isy-type">
            <h3 className="isy-type-title">
              {typeLabel(type.specialType)}
              <span>{type.pictures.length} {type.pictures.length === 1 ? 'Bild' : 'Bilder'}</span>
            </h3>
            {shown.length === 0 ? (
              <div className="pp-empty">Keine Events in diesem Filter.</div>
            ) : (
              <div className="ir-list">
                {shown.map((event) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    // The other dates of the same recurring event, whatever the filter shows.
                    series={event.series ? type.events.filter((each) => each.series === event.series && each.id !== event.id) : []}
                    pictures={type.pictures}
                    busy={busy.has(event.id)}
                    onAssign={assign}
                    onAssignAll={assignAll}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
      </>)}
    </section>
  );
}
